import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../context/AuthContext'
import Layout from '../components/Layout'

export default function NewTransaction() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [trusts, setTrusts] = useState([])
  const [accounts, setAccounts] = useState([])
  const [beneficiaries, setBeneficiaries] = useState([])
  const [categories, setCategories] = useState([])
  const [formData, setFormData] = useState({
    trust_id: '',
    account_id: '',
    type: 'income',
    date: new Date().toISOString().split('T')[0],
    description: '',
    amount: '',
    principal_amount: '',
    income_amount: '',
    allocation_basis: '',
    category: '',
    beneficiary_id: '',
    reconciled: false,
    corporate_action_type: '',
    corporate_action_details: {}
  })

  const isCorporateAction = formData.type === 'corporate_action'
  const isDisbursement = formData.type === 'disbursement'

  useEffect(() => {
    fetchInitialData()
  }, [])

  useEffect(() => {
    if (formData.trust_id) {
      fetchTrustData(formData.trust_id)
    }
  }, [formData.trust_id])

  // Auto-calculate total when principal/income change
  useEffect(() => {
    const principal = parseFloat(formData.principal_amount) || 0
    const income = parseFloat(formData.income_amount) || 0
    if (principal > 0 || income > 0) {
      setFormData(prev => ({ ...prev, amount: (principal + income).toFixed(2) }))
    }
  }, [formData.principal_amount, formData.income_amount])

  const fetchInitialData = async () => {
    const { data: memberData } = await supabase
      .from('trust_members')
      .select('trusts(*)')
      .eq('user_id', user.id)
    setTrusts(memberData?.map(d => d.trusts) || [])

    const { data: catData } = await supabase
      .from('categories')
      .select('*')
      .order('type')
    setCategories(catData || [])
  }

  const fetchTrustData = async (trustId) => {
    const { data: accountData } = await supabase
      .from('accounts')
      .select('*')
      .eq('trust_id', trustId)
    setAccounts(accountData || [])

    const { data: benData } = await supabase
      .from('beneficiaries')
      .select('*')
      .eq('trust_id', trustId)
      .eq('is_active', true)
    setBeneficiaries(benData || [])
  }

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setFormData({ ...formData, [e.target.name]: value })
  }

  const handleCorporateActionDetail = (key, value) => {
    setFormData({
      ...formData,
      corporate_action_details: {
        ...formData.corporate_action_details,
        [key]: value
      }
    })
  }

  const handleSubmit = async () => {
    setError(null)

    if (!formData.trust_id) { setError('Please select a trust'); return }
    if (!formData.account_id) { setError('Please select an account'); return }
    if (!formData.description.trim()) { setError('Description is required'); return }
    if (!formData.amount || parseFloat(formData.amount) <= 0) { setError('Amount must be greater than zero'); return }
    if (!formData.date) { setError('Date is required'); return }

    setLoading(true)

    const principal = parseFloat(formData.principal_amount) || 0
    const income = parseFloat(formData.income_amount) || 0
    const total = parseFloat(formData.amount) || 0

    const { data: txData, error: txError } = await supabase
      .from('transactions')
      .insert({
        trust_id: formData.trust_id,
        account_id: formData.account_id,
        type: formData.type,
        date: formData.date,
        description: formData.description.trim(),
        amount: total,
        principal_amount: principal,
        income_amount: income,
        allocation_basis: formData.allocation_basis || null,
        category: formData.category || null,
        beneficiary_id: formData.beneficiary_id || null,
        reconciled: formData.reconciled,
        corporate_action_type: isCorporateAction ? formData.corporate_action_type : null,
        corporate_action_details: isCorporateAction ? formData.corporate_action_details : null,
        entered_by: user.id
      })
      .select()
      .single()

    if (txError) {
      setError(txError.message)
      setLoading(false)
      return
    }

    // Update account balances
    const account = accounts.find(a => a.id === formData.account_id)
    if (account) {
      const isCredit = formData.type === 'income'
      const multiplier = isCredit ? 1 : -1

      await supabase
        .from('accounts')
        .update({
          principal_balance: (account.principal_balance || 0) + (principal * multiplier),
          income_balance: (account.income_balance || 0) + (income * multiplier),
          current_balance: (account.current_balance || 0) + (total * multiplier)
        })
        .eq('id', formData.account_id)
    }

    // Audit log
    await supabase.from('audit_log').insert({
      trust_id: formData.trust_id,
      user_id: user.id,
      action: 'created_transaction',
      table_affected: 'transactions',
      record_id: txData.id,
      new_value: txData
    })

    navigate('/transactions')
  }

  const incomeCategories = categories.filter(c => c.type === 'income')
  const expenseCategories = categories.filter(c => c.type === 'expense')

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">

        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/transactions')}
            className="text-gray-400 hover:text-gray-600 text-sm"
          >
            ← Back
          </button>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Add Transaction</h2>
            <p className="text-sm text-gray-500">Record a new trust transaction</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Transaction Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Transaction Type <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {['income', 'disbursement', 'transfer', 'fee', 'tax', 'corporate_action'].map(type => (
                <button
                  key={type}
                  onClick={() => setFormData({ ...formData, type })}
                  className={`py-2 px-3 rounded-lg text-xs font-medium border transition capitalize ${
                    formData.type === type
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-blue-300'
                  }`}
                >
                  {type.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>

          {/* Trust */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Trust <span className="text-red-500">*</span>
            </label>
            <select
              name="trust_id"
              value={formData.trust_id}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a trust...</option>
              {trusts.map(t => (
                <option key={t?.id} value={t?.id}>{t?.name}</option>
              ))}
            </select>
          </div>

          {/* Account */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account <span className="text-red-500">*</span>
            </label>
            <select
              name="account_id"
              value={formData.account_id}
              onChange={handleChange}
              disabled={!formData.trust_id}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
            >
              <option value="">Select an account...</option>
              {accounts.map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>

          {/* Date & Description */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select category...</option>
                {formData.type === 'income' ? (
                  incomeCategories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))
                ) : (
                  expenseCategories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))
                )}
              </select>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. Q3 dividend from Apple Inc."
            />
          </div>

          {/* Beneficiary (for disbursements) */}
          {isDisbursement && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Beneficiary
              </label>
              <select
                name="beneficiary_id"
                value={formData.beneficiary_id}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select beneficiary...</option>
                {beneficiaries.map(b => (
                  <option key={b.id} value={b.id}>{b.full_name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Principal & Income Allocation */}
          <div className="pt-2 border-t border-gray-100">
            <p className="text-sm font-medium text-gray-700 mb-3">
              Principal & Income Allocation
            </p>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Principal Amount
                </label>
                <input
                  type="number"
                  name="principal_amount"
                  value={formData.principal_amount}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Income Amount
                </label>
                <input
                  type="number"
                  name="income_amount"
                  value={formData.income_amount}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            {/* Total Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Amount <span className="text-red-500">*</span>
                <span className="text-gray-400 font-normal ml-1">
                  (auto-calculated from above, or enter directly)
                </span>
              </label>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>

            {/* Allocation Basis */}
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Allocation Basis <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                name="allocation_basis"
                value={formData.allocation_basis}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. UPIA §401 — dividends allocated to income"
              />
              <p className="text-xs text-gray-400 mt-1">
                Note the rule or trust provision governing this allocation
              </p>
            </div>
          </div>

          {/* Corporate Action Fields */}
          {isCorporateAction && (
            <div className="pt-2 border-t border-gray-100 space-y-4">
              <p className="text-sm font-medium text-gray-700">Corporate Action Details</p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Action Type
                </label>
                <select
                  name="corporate_action_type"
                  value={formData.corporate_action_type}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select type...</option>
                  <option value="stock_split">Stock Split</option>
                  <option value="merger">Merger</option>
                  <option value="tender_offer">Tender Offer</option>
                  <option value="spin_off">Spin-Off</option>
                  <option value="liquidation">Liquidation</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {formData.corporate_action_type === 'stock_split' && (
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Split Ratio</label>
                    <input type="text" placeholder="e.g. 2:1"
                      onChange={e => handleCorporateActionDetail('ratio', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Shares Before</label>
                    <input type="number" placeholder="0"
                      onChange={e => handleCorporateActionDetail('shares_before', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Shares After</label>
                    <input type="number" placeholder="0"
                      onChange={e => handleCorporateActionDetail('shares_after', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
              )}

              {formData.corporate_action_type === 'merger' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Acquiring Entity</label>
                    <input type="text" placeholder="Company name"
                      onChange={e => handleCorporateActionDetail('acquiring_entity', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Consideration Type</label>
                    <select
                      onChange={e => handleCorporateActionDetail('consideration_type', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select...</option>
                      <option value="cash">Cash</option>
                      <option value="stock">Stock</option>
                      <option value="mixed">Mixed</option>
                    </select>
                  </div>
                </div>
              )}

              {formData.corporate_action_type === 'tender_offer' && (
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Offer Price</label>
                    <input type="number" placeholder="0.00"
                      onChange={e => handleCorporateActionDetail('offer_price', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Shares Tendered</label>
                    <input type="number" placeholder="0"
                      onChange={e => handleCorporateActionDetail('shares_tendered', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Proration Factor</label>
                    <input type="text" placeholder="e.g. 0.85"
                      onChange={e => handleCorporateActionDetail('proration_factor', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
              )}

              {formData.corporate_action_type === 'spin_off' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">New Entity Name</label>
                    <input type="text" placeholder="Spun-off company"
                      onChange={e => handleCorporateActionDetail('new_entity', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Shares Received</label>
                    <input type="number" placeholder="0"
                      onChange={e => handleCorporateActionDetail('shares_received', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Reconciled */}
          <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
            <input
              type="checkbox"
              name="reconciled"
              id="reconciled"
              checked={formData.reconciled}
              onChange={handleChange}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <label htmlFor="reconciled" className="text-sm font-medium text-gray-700">
              Mark as reconciled
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving transaction...' : 'Save Transaction'}
            </button>
            <button
              onClick={() => navigate('/transactions')}
              className="px-6 border border-gray-300 text-gray-600 hover:bg-gray-50 font-medium py-2.5 rounded-lg transition"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </Layout>
  )
}